import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '@/components/dashboard/PageHeader';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, RefreshCw } from 'lucide-react';
import RemindersStatsBar from '@/components/reminders/RemindersStatsBar';
import {
  RemindersList,
  RemindersHistoryCard,
} from '@/components/reminders/RemindersList';
import SendReminderDialog from '@/components/reminders/SendReminderDialog';
import {
  fetchRemindersData,
  buildReminderQueue,
  buildReminderHistory,
  computeReminderStats,
  applyReminderFilter,
} from '@/lib/remindersData';

export default function RemindersPage() {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const data = await fetchRemindersData();
      setJobs(data);
    } catch (e) {
      setError(e?.message || t('reminders.loadError'));
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const queueRows = useMemo(() => buildReminderQueue(jobs), [jobs]);
  const stats = useMemo(() => computeReminderStats(queueRows), [queueRows]);
  const history = useMemo(() => buildReminderHistory(jobs), [jobs]);
  const filteredRows = useMemo(
    () => applyReminderFilter(queueRows, { search, status: statusFilter }),
    [queueRows, search, statusFilter]
  );

  const handleJobUpdated = (updatedJob) => {
    if (!updatedJob?.id) return;
    setJobs((cur) =>
      cur.map((j) => (j.id === updatedJob.id ? { ...j, ...updatedJob } : j))
    );
    // Refresh active row context (so reminders_sent count is current).
    setActiveRow((cur) => {
      if (!cur) return cur;
      const refreshed = buildReminderQueue(
        jobs.map((j) => (j.id === updatedJob.id ? { ...j, ...updatedJob } : j))
      ).find((r) => r.row_key === cur.row_key);
      return refreshed || cur;
    });
  };

  const handleSend = (row) => {
    setActiveRow(row);
    setDialogOpen(true);
  };

  return (
    <div data-testid="reminders-page">
      <PageHeader
        eyebrow={t('reminders.eyebrow')}
        title={t('reminders.title')}
        subtitle={t('reminders.subtitle')}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => load({ silent: true })}
            disabled={loading || refreshing}
            className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
            data-testid="reminders-refresh"
          >
            <RefreshCw size={14} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {t('reminders.refresh')}
          </Button>
        }
      />

      {error ? (
        <Alert
          className="mb-5 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
          data-testid="reminders-error"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      ) : null}

      <RemindersStatsBar stats={stats} />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2">
          <RemindersList
            rows={filteredRows}
            loading={loading}
            statusFilter={statusFilter}
            onStatusFilter={setStatusFilter}
            search={search}
            onSearch={setSearch}
            onSend={handleSend}
          />
        </div>
        <div>
          <RemindersHistoryCard history={history} />
        </div>
      </div>

      <SendReminderDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        row={activeRow}
        jobs={jobs}
        onLogged={handleJobUpdated}
      />
    </div>
  );
}
